import React, { useState } from "react";
import { Form, Input, Select, Button, Upload, message, Spin } from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  CameraOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  CheckCircleFilled,
  SafetyCertificateOutlined,
  TeamOutlined,
  DollarOutlined,
  DashboardOutlined,
  StarFilled,
  HeartFilled,
  RocketOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { staffPublicRegister } from "../../../../Api/CoreApi";
import LoginHeader from "../../../../Home_Page_WLog/LoginHeader/LoginHeader";
import Footer from "../../../../Home_Page_WLog/Footer/Footer";
import Swal from "sweetalert2";
import "./StaffRegister.css";

const { Option } = Select;

function StaffRegister() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("username", values.username.trim().toLowerCase());
      formData.append("password", values.password);
      formData.append("email", values.email.trim());
      formData.append("contact", values.contact.trim());
      formData.append("first_name", values.first_name.trim());
      formData.append("gender", values.gender || "");
      formData.append("district", values.district || "");
      formData.append("state", values.state || "");
      if (file) formData.append("pic", file);

      const response = await staffPublicRegister(formData);

      if (response.success) {
        Swal.fire({
          title: "🎉 Application Submitted!",
          html: `
            <div style="text-align:center; padding:10px;">
              <p style="font-size:15px; color:#333; margin-bottom:12px;">
                Your staff application has been submitted successfully.
              </p>
              <div style="background:#fff0f5; padding:16px; border-radius:16px; border:1px solid #ffb3d0; margin:16px 0;">
                <p style="font-size:13px; color:#c2185b; margin:0;">
                  ⏳ <strong>Please wait for admin approval.</strong><br/>
                  You will receive an <strong>email</strong> and <strong>SMS</strong> once approved.
                </p>
              </div>
              <div style="background:#fce4ec; padding:14px; border-radius:14px; margin-top:12px;">
                <p style="margin:0; font-size:13px; color:#880e4f;">
                  <strong>Your Username:</strong> ${values.username.toLowerCase()}<br/>
                  <strong>Profile ID:</strong> ${response.profile_id || "Will be assigned"}
                </p>
              </div>
            </div>
          `,
          icon: "success",
          confirmButtonColor: "#e91e63",
          confirmButtonText: "Go to Login Page",
        }).then(() => {
          navigate("/User_Login");
        });

        form.resetFields();
        setFile(null);
        setPreviewUrl(null);
      }
    } catch (error) {
      const errorMsg = error.error || error.message || "Registration failed";
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (f) => {
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    return false;
  };

  const floatingElements = [
    { emoji: "💼", top: "8%", left: "5%" },
    { emoji: "👨‍💼", top: "15%", left: "14%" },
    { emoji: "🤝", top: "22%", left: "88%" },
    { emoji: "⭐", top: "36%", left: "3%" },
    { emoji: "📋", top: "50%", left: "92%" },
    { emoji: "✅", top: "64%", left: "8%" },
    { emoji: "💪", top: "72%", left: "85%" },
    { emoji: "🎯", top: "85%", left: "12%" },
    { emoji: "💖", top: "45%", left: "6%" },
    { emoji: "🌟", top: "78%", left: "90%" },
  ];

  const features = [
    { icon: <DollarOutlined />, text: "Earn commission on every match" },
    { icon: <TeamOutlined />, text: "Manage your own user base" },
    { icon: <DashboardOutlined />, text: "Track earnings & withdrawals" },
    { icon: <RocketOutlined />, text: "Professional dashboard access" },
  ];

  const stats = [
    { icon: "💰", label: "Commission", value: "15%" },
    { icon: "👥", label: "Users", value: "Unlimited" },
    { icon: "📊", label: "Dashboard", value: "Full Access" },
  ];

  const trustItems = ["Secure", "Reviewed by Admin", "Trusted Platform"];

  return (
    <>
      <LoginHeader />

      <div className="sr-page-wrapper">
        {/* Background Layers */}
        <div className="sr-bg-gradient" />
        <div className="sr-bg-pattern" />
        <div className="sr-bg-glow sr-bg-glow-1" />
        <div className="sr-bg-glow sr-bg-glow-2" />
        <div className="sr-bg-glow sr-bg-glow-3" />

        {/* Floating Emojis */}
        {floatingElements.map((item, i) => (
          <span
            key={i}
            className="sr-floating-emoji"
            style={{
              top: item.top,
              left: item.left,
              fontSize: `${18 + (i % 5) * 5}px`,
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${3.5 + i * 0.3}s`,
            }}
          >
            {item.emoji}
          </span>
        ))}

        {/* Floating Particles */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="sr-particle"
            style={{
              top: `${10 + i * 15}%`,
              left: `${5 + i * 16}%`,
              animationDelay: `${i * 0.6}s`,
              width: `${6 + (i % 3) * 4}px`,
              height: `${6 + (i % 3) * 4}px`,
            }}
          />
        ))}

        <Spin spinning={loading} tip="Submitting application..." size="large">
          <div className="sr-main-container">
            <div className="sr-card sr-card-animate">
              {/* ========== LEFT PANEL ========== */}
              <div className="sr-left-panel">
                <div className="sr-left-overlay" />
                <div className="sr-left-decor-circle sr-left-decor-1" />
                <div className="sr-left-decor-circle sr-left-decor-2" />
                <div className="sr-left-decor-circle sr-left-decor-3" />

                <div className="sr-left-content">
                  {/* Badge */}
                  <div className="sr-badge">
                    <SafetyCertificateOutlined className="sr-badge-icon" />
                    <span>Staff Registration</span>
                    <div className="sr-badge-dot" />
                  </div>

                  {/* Title */}
                  <h1 className="sr-main-title">
                    Join Our
                    <br />
                    <span className="sr-title-highlight">Matchmaking</span>
                    <br />
                    <span className="sr-title-highlight-2">Team</span>
                  </h1>

                  {/* Description */}
                  <p className="sr-description">
                    Apply to become a staff member and help people find their
                    perfect life partner. Your application will be reviewed by
                    our admin team.
                  </p>

                  {/* Features */}
                  <div className="sr-features-list">
                    {features.map((feat, index) => (
                      <div
                        key={index}
                        className="sr-feature-row"
                        style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                      >
                        <div className="sr-feature-icon-wrap">
                          <CheckCircleFilled />
                        </div>
                        <span className="sr-feature-text">{feat.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="sr-stats-row">
                    {stats.map((stat, i) => (
                      <div key={i} className="sr-stat-card">
                        <span className="sr-stat-emoji">{stat.icon}</span>
                        <div className="sr-stat-info">
                          <span className="sr-stat-value">{stat.value}</span>
                          <span className="sr-stat-label">{stat.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Bottom Hearts */}
                  <div className="sr-left-hearts">
                    <HeartFilled className="sr-heart sr-heart-1" />
                    <HeartFilled className="sr-heart sr-heart-2" />
                    <HeartFilled className="sr-heart sr-heart-3" />
                  </div>
                </div>
              </div>

              {/* ========== RIGHT PANEL ========== */}
              <div className="sr-right-panel">
                {/* Shimmer Top Bar */}
                <div className="sr-shimmer-bar" />

                {/* Decorative Corner */}
                <div className="sr-right-corner-decor" />

                {/* Form Header */}
                <div className="sr-form-header">
                  <div className="sr-header-icon-wrap">
                    <div className="sr-header-icon-ring" />
                    <IdcardOutlined className="sr-header-icon" />
                  </div>
                  <h3 className="sr-form-title">Staff Application</h3>
                  <p className="sr-form-subtitle">
                    Fill in your details to apply as a staff member
                  </p>
                  <div className="sr-header-divider">
                    <StarFilled className="sr-divider-star" />
                  </div>
                </div>

                {/* Form Container */}
                <div className="sr-form-container">
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark={false}
                    className="sr-form"
                  >
                    {/* Photo Upload */}
                    <div className="sr-photo-section">
                      <Upload
                        beforeUpload={handleFileUpload}
                        showUploadList={false}
                      >
                        <div
                          className="sr-photo-upload"
                          style={
                            previewUrl
                              ? {
                                  backgroundImage: `url(${previewUrl})`,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                }
                              : {}
                          }
                        >
                          {!previewUrl && (
                            <div className="sr-photo-placeholder">
                              <CameraOutlined className="sr-camera-icon" />
                              <span>Photo</span>
                            </div>
                          )}
                          <div className="sr-photo-badge">
                            <CameraOutlined />
                          </div>
                        </div>
                      </Upload>
                      <p className="sr-photo-hint">
                        Upload Photo <span>(Optional)</span>
                      </p>
                    </div>

                    {/* Form Grid */}
                    <div className="sr-form-grid">
                      {/* Username */}
                      <div className="sr-form-field sr-field-full">
                        <Form.Item
                          name="username"
                          label={
                            <span className="sr-label">
                              <UserOutlined className="sr-label-icon" />
                              Username <span className="sr-required">*</span>
                            </span>
                          }
                          rules={[
                            { required: true, message: "Username is required" },
                            { min: 4, message: "Minimum 4 characters" },
                            {
                              pattern: /^[a-z0-9]+$/,
                              message: "Only lowercase letters & numbers",
                            },
                          ]}
                        >
                          <Input
                            prefix={<UserOutlined className="sr-input-icon" />}
                            placeholder="Choose a unique username"
                            className="sr-input"
                          />
                        </Form.Item>
                      </div>

                      {/* Password */}
                      <div className="sr-form-field sr-field-full">
                        <Form.Item
                          name="password"
                          label={
                            <span className="sr-label">
                              <LockOutlined className="sr-label-icon" />
                              Password <span className="sr-required">*</span>
                            </span>
                          }
                          rules={[
                            { required: true, message: "Password is required" },
                            { min: 8, message: "Minimum 8 characters" },
                          ]}
                        >
                          <Input.Password
                            prefix={<LockOutlined className="sr-input-icon" />}
                            placeholder="Create a strong password"
                            className="sr-input"
                          />
                        </Form.Item>
                      </div>

                      {/* Full Name */}
                      <div className="sr-form-field sr-field-full">
                        <Form.Item
                          name="first_name"
                          label={
                            <span className="sr-label">
                              <IdcardOutlined className="sr-label-icon" />
                              Full Name <span className="sr-required">*</span>
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Full name is required",
                            },
                          ]}
                        >
                          <Input
                            prefix={
                              <IdcardOutlined className="sr-input-icon" />
                            }
                            placeholder="Enter your full name"
                            className="sr-input"
                          />
                        </Form.Item>
                      </div>

                      {/* Email */}
                      <div className="sr-form-field sr-field-full">
                        <Form.Item
                          name="email"
                          label={
                            <span className="sr-label">
                              <MailOutlined className="sr-label-icon" />
                              Email Address{" "}
                              <span className="sr-required">*</span>
                            </span>
                          }
                          rules={[
                            { required: true, message: "Email is required" },
                            { type: "email", message: "Enter a valid email" },
                          ]}
                        >
                          <Input
                            prefix={<MailOutlined className="sr-input-icon" />}
                            placeholder="your.email@example.com"
                            className="sr-input"
                          />
                        </Form.Item>
                      </div>

                      {/* Mobile */}
                      <div className="sr-form-field sr-field-half">
                        <Form.Item
                          name="contact"
                          label={
                            <span className="sr-label">
                              <PhoneOutlined className="sr-label-icon" />
                              Mobile <span className="sr-required">*</span>
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Mobile number required",
                            },
                            {
                              pattern: /^[0-9]{10}$/,
                              message: "Must be 10 digits",
                            },
                          ]}
                          normalize={(v) => v?.replace(/\D/g, "").slice(0, 10)}
                        >
                          <Input
                            prefix={<PhoneOutlined className="sr-input-icon" />}
                            placeholder="10-digit number"
                            maxLength={10}
                            className="sr-input"
                          />
                        </Form.Item>
                      </div>

                      {/* Gender */}
                      <div className="sr-form-field sr-field-half">
                        <Form.Item
                          name="gender"
                          label={
                            <span className="sr-label">
                              <UserOutlined className="sr-label-icon" />
                              Gender <span className="sr-required">*</span>
                            </span>
                          }
                          rules={[
                            { required: true, message: "Select your gender" },
                          ]}
                        >
                          <Select
                            placeholder="Select gender"
                            className="sr-select"
                          >
                            <Option value="Male">👨 Male</Option>
                            <Option value="Female">👩 Female</Option>
                            <Option value="Other">⚧ Other</Option>
                          </Select>
                        </Form.Item>
                      </div>

                      {/* City */}
                      <div className="sr-form-field sr-field-half">
                        <Form.Item
                          name="district"
                          label={
                            <span className="sr-label">
                              <EnvironmentOutlined className="sr-label-icon" />
                              City / District
                            </span>
                          }
                        >
                          <Input
                            prefix={
                              <EnvironmentOutlined className="sr-input-icon" />
                            }
                            placeholder="Enter your city"
                            className="sr-input"
                          />
                        </Form.Item>
                      </div>

                      {/* State */}
                      <div className="sr-form-field sr-field-half">
                        <Form.Item
                          name="state"
                          label={
                            <span className="sr-label">
                              <EnvironmentOutlined className="sr-label-icon" />
                              State
                            </span>
                          }
                        >
                          <Input
                            prefix={
                              <EnvironmentOutlined className="sr-input-icon" />
                            }
                            placeholder="Enter your state"
                            className="sr-input"
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <Form.Item className="sr-submit-section">
                      <Button
                        htmlType="submit"
                        loading={loading}
                        block
                        className="sr-submit-btn"
                      >
                        <span className="sr-btn-content">
                          <SafetyCertificateOutlined className="sr-btn-icon" />
                          <span>Submit Application</span>
                        </span>
                        <div className="sr-btn-shimmer" />
                      </Button>
                    </Form.Item>
                  </Form>
                </div>

                {/* Login Link */}
                <div className="sr-login-link-section">
                  <div className="sr-link-divider">
                    <span className="sr-link-divider-line" />
                    <span className="sr-link-divider-text">or</span>
                    <span className="sr-link-divider-line" />
                  </div>
                  <a href="/User_Login" className="sr-login-link">
                    Already have an account?{" "}
                    <strong>Login here</strong>
                  </a>
                </div>

                {/* Trust Badges */}
                <div className="sr-trust-section">
                  {trustItems.map((item, i) => (
                    <div key={i} className="sr-trust-badge">
                      <CheckCircleFilled className="sr-trust-icon" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Spin>
      </div>

      <Footer />
    </>
  );
}

export default StaffRegister;